import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { toast } from "sonner";
import { AlertTriangle, UserPlus, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/dates";

// Shown to schools once we've asked them (120 days out) to name the student on a held rotation.
export const StudentsToName = ({ rows = [], onSaved }) => {
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  if (rows.length === 0) return null;

  const open = (r) => { setTarget(r); setForm({}); };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Please enter the student's full name.");
    if (!form.personal_email?.trim() && !form.school_email?.trim()) return toast.error("Please enter an email address for the student.");
    setSaving(true);
    try {
      await api.post(`/placement-requests/${target.pr_id}/student-details`, form);
      toast.success(`${form.name} added — your Campus Coordinator has been notified`);
      setTarget(null);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="mb-5 rounded-md border-l-4 border-cc-orange bg-orange-50 px-5 py-4" data-testid="students-to-name-card">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-cc-orange mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-orange-900">
              {rows.length} rotation{rows.length > 1 ? "s" : ""} still need{rows.length > 1 ? "" : "s"} a student name
            </p>
            <p className="text-sm text-orange-800/80 mt-0.5">
              Add the student's name and contact info so we can start their paperwork and clear them in time.
            </p>
            <div className="mt-3 space-y-2">
              {rows.map((r) => (
                <div key={r.pr_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-white border border-orange-200 px-3 py-2"
                  data-testid={`to-name-row-${r.pr_id}`}>
                  <div className="min-w-0 text-sm">
                    <span className="font-medium text-zinc-800">{r.discipline} {r.rotation_level}</span>
                    <span className="text-zinc-500"> · {r.facility_name || "facility TBD"}</span>
                    <div className="text-xs text-zinc-500">
                      Starts {fmtDate(r.rotation_start)} · {r.days_until} day{r.days_until === 1 ? "" : "s"} away
                    </div>
                  </div>
                  <Button size="sm" className="bg-cc-orange hover:bg-orange-600 text-white shrink-0"
                    onClick={() => open(r)} data-testid={`add-student-details-${r.pr_id}`}>
                    <UserPlus className="w-4 h-4 mr-1.5" />Add student details
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="max-w-md" data-testid="student-details-dialog">
          <DialogHeader>
            <DialogTitle>Who is filling this rotation?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500 -mt-2">
            {target?.discipline} {target?.rotation_level} starting {fmtDate(target?.rotation_start)}
            {target?.facility_name ? ` at ${target.facility_name}` : ""}.
          </p>
          <div className="space-y-3">
            <Field label="Student full name">
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe" data-testid="student-details-name" />
            </Field>
            <Field label="Personal email">
              <Input type="email" value={form.personal_email || ""} onChange={(e) => setForm({ ...form, personal_email: e.target.value })}
                placeholder="jane@email.com" data-testid="student-details-personal-email" />
            </Field>
            <Field label="School email">
              <Input type="email" value={form.school_email || ""} onChange={(e) => setForm({ ...form, school_email: e.target.value })}
                placeholder="jane@school.edu" data-testid="student-details-school-email" />
            </Field>
            <Field label="Cell number">
              <Input value={form.cell_phone || ""} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })}
                placeholder="(555) 123-4567" data-testid="student-details-cell" />
            </Field>
            <Field label="Expected graduation">
              <Input type="date" value={form.graduation_date || ""} onChange={(e) => setForm({ ...form, graduation_date: e.target.value })}
                data-testid="student-details-graduation" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} data-testid="student-details-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="student-details-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
